import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Loader } from '@aws-amplify/ui-react';
import { API } from 'aws-amplify';
import { FaHome, FaArrowUp, FaMusic } from 'react-icons/fa';

function PlaylistViewer() {
  const { playlistId } = useParams();
  const location = useLocation();
  const [playlist, setPlaylist] = useState(location.state?.playlist || null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isHtml, setIsHtml] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => { fetchPlaylistContent(); }, [playlistId]);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 200);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchPlaylistContent = async () => {
    if (!playlistId) { setError('Playlist ID is required'); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const response = await API.get('PlaylistsAPI', 'playlist-content', {
        queryStringParameters: { id: playlistId },
        headers: { 'Accept': 'application/json' }
      });
      if (response) {
        if (response.playlist) setPlaylist(response.playlist);
        setContent(response.content || '');
        setIsHtml(response.isHtml || false);
      } else {
        setError('Failed to load playlist content');
      }
    } catch (err) {
      setError('Failed to load playlist content: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="np-loading-wrap">
        <Loader size="large" />
        <p className="np-loading-text">🪷 Loading playlist…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="np-error-box" style={{ margin: '2rem auto', maxWidth: 500 }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🙏</div>
        <p>{error}</p>
        <Link to="/" className="np-btn" style={{ marginTop: '1rem', display: 'inline-flex' }}>⬅ Back to Home</Link>
      </div>
    );
  }

  return (
    <div>
      {/* ── Playlist header card ── */}
      <div className="np-panel" style={{
        marginBottom: '1.25rem',
        background: 'linear-gradient(135deg, #F0C040 0%, #FFE082 50%, #E8A800 100%)',
        border: '2px solid var(--gold-deep)',
        boxShadow: '0 4px 18px rgba(184,134,11,0.25)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '2.8rem', height: '2.8rem', borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, var(--brown-deep), var(--maroon))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.25)'
            }}>
              <FaMusic style={{ color: 'var(--gold-bright)', fontSize: '1.1rem' }} />
            </div>
            <div>
              <h1 style={{
                margin: 0, fontFamily: 'var(--font-heading)', fontSize: 'clamp(1rem, 3vw, 1.4rem)',
                color: 'var(--brown-deep)', letterSpacing: '1px', fontWeight: 700
              }}>
                {playlist?.name || `Playlist ${playlistId}`}
              </h1>
              {playlist && (
                <span style={{ fontSize: '0.7rem', color: 'var(--brown-mid)', fontFamily: 'var(--font-ui)', letterSpacing: '0.5px' }}>
                  ID: {playlist.id}
                </span>
              )}
            </div>
          </div>

          <Link
            to="/"
            className="np-btn"
            style={{ background: 'var(--brown-deep)', borderColor: 'var(--gold-deep)', color: 'var(--gold-light)', gap: '0.4rem' }}
          >
            <FaHome style={{ fontSize: '0.85rem' }} /> Home
          </Link>
        </div>
      </div>

      <hr className="np-divider" />

      {/* ── Playlist content ── */}
      <div
        className="np-content-area"
        style={{ lineHeight: '1.9', fontSize: '0.98rem' }}
      >
        {isHtml
          ? <div dangerouslySetInnerHTML={{ __html: content }} />
          : content
        }
      </div>

      {/* Scroll to top */}
      {showScrollTop && (
        <button className="np-scroll-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Scroll to top">
          <FaArrowUp />
        </button>
      )}
    </div>
  );
}

export default PlaylistViewer;
